import { Alert, Box, Chip, MenuItem, Select, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import type { AccuracyModelOption } from '../../types/response';
import { getErrorMessage } from '../../utils/extractErrorMessage';
import { SectionShell } from '../SectionShell/SectionShell';

interface ExtractionModelSectionProps {
  models: AccuracyModelOption[];
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  selectedModelId: string | null;
  isComplete: boolean;
  isBatchSelected: boolean;
  onSelectModel: (modelId: string) => void;
}

export const ExtractionModelSection = ({
  models,
  isLoading,
  isError,
  error,
  selectedModelId,
  isComplete,
  isBatchSelected,
  onSelectModel,
}: ExtractionModelSectionProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const serverErrorMessage = getErrorMessage(error);

  return (
    <SectionShell
      stepNumber={2}
      title={formatMessage({ id: 'adminProjects.generateAccuracy.model.title' })}
      description={formatMessage({ id: 'adminProjects.generateAccuracy.model.description' })}
      isComplete={isComplete}
    >
      {isError ? (
        <Alert severity="error">
          {serverErrorMessage ||
            formatMessage({ id: 'adminProjects.generateAccuracy.model.loadError' })}
        </Alert>
      ) : (
        <Select
          fullWidth
          displayEmpty
          disabled={isLoading || !isBatchSelected}
          value={selectedModelId ?? ''}
          onChange={(e) => onSelectModel(e.target.value)}
          renderValue={(value) => {
            if (!isBatchSelected) {
              return (
                <Typography color="text.secondary">
                  {formatMessage({ id: 'adminProjects.generateAccuracy.model.selectABatchFirst' })}
                </Typography>
              );
            }
            if (!value) {
              return (
                <Typography color="text.secondary">
                  {formatMessage({ id: 'adminProjects.generateAccuracy.model.placeholder' })}
                </Typography>
              );
            }
            return models.find((m) => m.modelId === value)?.displayName ?? '';
          }}
        >
          {models.map((model) => (
            <MenuItem key={model.modelId} value={model.modelId}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing(4),
                  }}
                >
                  <Typography>{model.displayName}</Typography>
                  {model.isRecommended && (
                    <Chip
                      size="small"
                      label={formatMessage({
                        id: 'adminProjects.generateAccuracy.recommended',
                      })}
                      sx={{
                        backgroundColor: theme.palette.primary.light,
                        color: theme.palette.primary.main,
                        fontSize: '12px',
                      }}
                    />
                  )}
                </Box>
                <Typography variant="body1" color="text.secondary">
                  {model.provider}
                  {model.isOpenWeights
                    ? ` · ${formatMessage({
                        id: 'adminProjects.generateAccuracy.model.openWeights',
                      })}`
                    : ''}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      )}
    </SectionShell>
  );
};
